import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { InitialAvatar } from './sessionStatus';

export interface OpenTab {
  id: string;
  name: string;
}

// The strip of open sessions above the detail. Opening a session from the left
// list adds a tab; clicking a tab switches the active session; the X closes it.
export function SessionTabsBar({
  tabs,
  activeId,
  onSelect,
  onClose,
}: {
  tabs: OpenTab[];
  activeId?: string;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
}) {
  if (tabs.length === 0) return null;

  return (
    <div className="flex shrink-0 items-end overflow-x-auto border-b border-[#E4E4E4] bg-[#E6EAF8]/60 hide-scrollbar">
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        return (
          <div
            key={tab.id}
            role="tab"
            tabIndex={0}
            aria-selected={active}
            className={cn(
              'group relative flex h-[46px] min-w-[180px] max-w-[300px] flex-1 cursor-pointer items-center gap-2 rounded-t-lg pl-4 pr-10 transition-colors',
              active
                ? 'z-10 bg-white text-[#041A3A]'
                : 'text-[#5F6368] hover:bg-black/5 hover:text-[#1A2530]',
            )}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(tab.id);
              }
            }}
          >
            <InitialAvatar name={tab.name} size={24} />
            <span
              className={cn(
                'truncate text-[length:var(--text-14)]',
                active ? 'font-semibold text-[#041A3A]' : 'font-medium text-[#666666]',
              )}
            >
              {tab.name}
            </span>
            <button
              type="button"
              aria-label={`Close ${tab.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.id);
              }}
              className={cn(
                'absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#0048FF] transition-opacity',
                active
                  ? 'bg-[#E5EDFF] opacity-100 hover:bg-black/5'
                  : 'bg-[#E5EDFF] opacity-0 group-hover:opacity-100 hover:bg-black/10',
              )}
            >
              <X size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
